import { useCallback, useEffect, useState } from 'react';
import { nuclearReload } from './nuclearReload';
import { fetchServerBuildId, isNewerBuild } from './version';

/** Chemin du service worker généré au build (cf. plugin `serviceWorkerPlugin`). */
const SW_URL = '/sw.js';

/** Périodicité du sondage du `buildId` serveur (ms). */
const POLL_INTERVAL_MS = 60_000;

interface AppUpdateState {
  /** `true` dès qu'une nouvelle version est détectée (SW en attente OU buildId serveur différent). */
  updateAvailable: boolean;
  /** Déclenche le rechargement nucléaire (purge caches + SW puis reload). */
  reload: () => Promise<void>;
}

/**
 * Détection de nouvelle version + enregistrement du service worker.
 *
 * Deux signaux convergent sur `updateAvailable` :
 *  1. **SW** — un nouveau worker atteint l'état `installed`/`waiting` (nouvel asset déployé).
 *  2. **buildId** — le `buildId` de `/api/health` diffère de celui embarqué au build
 *     (sondage périodique + au retour de focus/visibilité).
 *
 * **Inerte en dev et en test** : aucun enregistrement de SW, aucun sondage, hors
 * `import.meta.env.PROD` ou si `navigator.serviceWorker` est absent (happy-dom).
 */
export function useAppUpdate(): AppUpdateState {
  const [updateAvailable, setUpdateAvailable] = useState(false);

  useEffect(() => {
    // Garde dev/test : pas de SW ni de sondage hors production navigateur.
    if (!import.meta.env.PROD) return;
    if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return;

    let cancelled = false;
    const controller = new AbortController();
    const flag = (): void => {
      if (!cancelled) setUpdateAvailable(true);
    };

    /**
     * Détachements à exécuter au démontage (audit F7).
     *
     * `ServiceWorkerRegistration` et `ServiceWorker` sont des objets du NAVIGATEUR :
     * ils SURVIVENT au démontage du composant. Sans retrait explicite, chaque montage
     * empilait un `updatefound` (et ses `statechange` imbriqués) dont la closure
     * retenait une instance morte — fuite pure, plus un `setUpdateAvailable` sur un
     * composant démonté à chaque déploiement détecté.
     */
    const teardown: (() => void)[] = [];

    // 1) Service worker : enregistrement + détection d'un worker en attente.
    void navigator.serviceWorker
      .register(SW_URL)
      .then((registration) => {
        // Démonté pendant l'enregistrement : ne rien brancher (le `teardown` a déjà
        // été purgé, tout ajout ultérieur ne serait jamais retiré).
        if (cancelled) return;
        if (registration.waiting) flag();
        const onUpdateFound = (): void => {
          const installing = registration.installing;
          if (!installing) return;
          const onStateChange = (): void => {
            // `installed` + un contrôleur déjà présent ⇒ mise à jour prête.
            if (installing.state === 'installed' && navigator.serviceWorker.controller) flag();
          };
          installing.addEventListener('statechange', onStateChange);
          teardown.push(() => installing.removeEventListener('statechange', onStateChange));
        };
        registration.addEventListener('updatefound', onUpdateFound);
        teardown.push(() => registration.removeEventListener('updatefound', onUpdateFound));
      })
      .catch(() => {
        /* enregistrement best-effort : le signal buildId prend le relais */
      });

    // 2) buildId : sondage périodique + au retour de focus/visibilité.
    const checkBuild = async (): Promise<void> => {
      const serverBuildId = await fetchServerBuildId(controller.signal);
      if (isNewerBuild(serverBuildId)) flag();
    };
    void checkBuild();
    const interval = window.setInterval(() => void checkBuild(), POLL_INTERVAL_MS);
    const onVisible = (): void => {
      if (document.visibilityState === 'visible') void checkBuild();
    };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      cancelled = true;
      controller.abort();
      window.clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisible);
      for (const detach of teardown) detach();
      teardown.length = 0;
    };
  }, []);

  const reload = useCallback(() => nuclearReload(), []);

  return { updateAvailable, reload };
}
